import { useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useMutation } from '@tanstack/react-query';
import { selectSession } from '@client/model/selectors';
import { wealthboxApi } from '@client/api/integrations';

export function WealthboxOAuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const session = useSelector(selectSession);
  const code = searchParams.get('code');
  const exchangedRef = useRef(false);
  const isSessionReady = !!session.auth0Session?.accessToken;
  const advisorId = session.advisor?.advisorId;

  const exchangeMutation = useMutation({
    mutationFn: (code: string) => wealthboxApi.oauth.exchangeCode({ code }),
  });

  useEffect(() => {
    if (code && !exchangedRef.current && isSessionReady) {
      exchangedRef.current = true;
      exchangeMutation.mutate(code);
    }
  }, [code, isSessionReady]);

  useEffect(() => {
    if (exchangeMutation.isSuccess && advisorId) {
      navigate(`/advisors/${advisorId}/integrations`);
    }
  }, [exchangeMutation.isSuccess, advisorId, navigate]);

  if (!code) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="rounded-lg border border-red-200 bg-red-50 p-8 text-center">
          <p className="text-red-800">No authorization code found. Please try connecting again.</p>
        </div>
      </div>
    );
  }

  if (exchangeMutation.isError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="rounded-lg border border-red-200 bg-red-50 p-8 text-center">
          <p className="mb-2 text-lg font-semibold text-red-800">Connection Failed</p>
          <p className="text-sm text-red-700">
            {exchangeMutation.error?.message ?? 'Failed to connect to Wealthbox'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-primary" />
        <p className="text-gray-700">Connecting to Wealthbox...</p>
      </div>
    </div>
  );
}
